import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ClockIcon, WifiOffIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { founder } from '../../data/content';
import { ease } from '../../core/format/motion';
import { ApiError, confirmNewsletter } from '../../utils/api';
import type { IconComponent } from '../../ui/icon';
type Phase = 'checking' | 'done' | 'expired' | 'error';
const COPY: Record<Phase, {
  title: string;
  text: string;
  icon: IconComponent | null;
}> = {
  checking: {
    icon: null,
    title: 'Even kijken…',
    text: 'We bevestigen je aanmelding.'
  },
  done: {
    icon: CheckIcon,
    title: 'Je staat op de lijst.',
    text: `De vijf fouten komen zo in je inbox. Daarna één keer per maand wat ${founder.firstName} in de salon opviel. Afmelden kan onderaan elke mail.`
  },
  expired: {
    icon: ClockIcon,
    title: 'Deze link is verlopen of al gebruikt.',
    text: 'Meld je gerust opnieuw aan, onderaan deze pagina. Je krijgt dan een nieuwe bevestigingsmail.'
  },
  error: {
    icon: WifiOffIcon,
    title: 'Je aanmelding kon niet worden bevestigd',
    text: 'Controleer je verbinding en probeer het opnieuw. De link uit je mail blijft werken.'
  }
};

/** Het token uit de adresbalk, één keer gelezen (vóór het daar wordt weggehaald). */
const readToken = () => new URLSearchParams(window.location.search).get('token')?.trim() ?? '';

/**
 * /nieuwsbrief/bevestig?token=… — de klik uit de bevestigingsmail (dubbele opt-in).
 * Het openen van de pagina doet niets: pas de POST vanuit de browser bevestigt. Zo kan een
 * mailscanner die de link "voorproeft" niemand per ongeluk aanmelden. Het token gaat meteen
 * uit de adresbalk, zodat het niet in de geschiedenis of een gedeelde link blijft staan.
 */
export function NewsletterConfirm() {
  const [token] = useState(readToken);
  const [phase, setPhase] = useState<Phase>(token ? 'checking' : 'expired');
  /** Ophogen = opnieuw proberen. */
  const [round, setRound] = useState(0);
  /** Eén POST per ronde, ook als een effect in ontwikkeling twee keer draait (een token is eenmalig). */
  const sentRound = useRef<number | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const reduce = useReducedMotion();

  // Token uit de adresbalk, zoals bij ?inloglink= en ?verifieer= (AccountContext).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);
  useEffect(() => {
    if (!token || sentRound.current === round) return;
    sentRound.current = round;
    setPhase('checking');
    confirmNewsletter({
      token
    }).then(() => setPhase('done'), (err: unknown) => setPhase(err instanceof ApiError && err.status === 422 && err.code === 'link_expired' ? 'expired' : 'error'));
  }, [token, round]);

  // Elke nieuwe stand: focus op de kop, zodat schermlezers hem voorlezen.
  useEffect(() => {
    headingRef.current?.focus({
      preventScroll: true
    });
  }, [phase]);
  const toSignup = () => {
    const field = document.getElementById('footer-email');
    field?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'center'
    });
    field?.focus({
      preventScroll: true
    });
  };
  const copy = COPY[phase];
  const Icon = copy.icon;
  return <section className="bg-sand" data-id="element-7965" data-mp="2vmmfhe57hxvx">
      <div className="mx-auto max-w-2xl px-6 pb-28 pt-12 lg:px-10 lg:pt-20" aria-busy={phase === 'checking'} data-id="element-7966" data-mp="2vljo753z1tfh">
        <motion.div key={phase} initial={reduce ? false : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-7967" data-mp="35d78mm242a7g">
          <span aria-hidden="true" className={`flex h-12 w-12 items-center justify-center rounded-full ${phase === 'done' ? 'bg-ink text-shell' : 'bg-shell text-clay ring-1 ring-line'}`} data-id="element-7968" data-mp="3jdrujr3bom56">
            {Icon ? <Icon className="h-5 w-5" strokeWidth={1.5} data-id="element-7969" data-mp="1cmlqoh6h6po1" /> : <Spinner className="h-5 w-5" data-id="element-7970" data-mp="1hn11p23abl2u" />}
          </span>

          <h1 ref={headingRef} tabIndex={-1} className="mt-7 max-w-xl font-display text-[2.2rem] font-light leading-[1.08] text-ink sm:text-5xl" data-id="element-7971" data-mp="1hlganmg6ik9q">
            {copy.title}
          </h1>
          <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" role="status" data-id="element-7972" data-mp="1rfow5q6slcye">
            {copy.text}
          </p>

          {phase !== 'checking' && <div className="mt-9 flex flex-wrap items-center gap-3" data-id="element-7973" data-mp="ypq55p91f5r3">
              {phase === 'done' && <Link to="/webshop" className={btnPrimary} data-id="element-7974" data-mp="13mglmdor83d1">
                  Naar de webshop
                </Link>}
              {phase === 'expired' && <>
                  <button type="button" className={btnPrimary} onClick={toSignup} data-id="element-7975" data-mp="2f2nb2jf9kcu7">
                    Opnieuw aanmelden
                  </button>
                  <Link to="/webshop" className={btnSecondary} data-id="element-7976" data-mp="7h6s5ge28cka">
                    Naar de webshop
                  </Link>
                </>}
              {phase === 'error' && <>
                  <button type="button" className={btnPrimary} onClick={() => setRound(r => r + 1)} data-id="element-7977" data-mp="3utpktfzfihiq">
                    Probeer opnieuw
                  </button>
                  <Link to="/webshop" className={btnSecondary} data-id="element-7978" data-mp="3ptlak7p46j0y">
                    Naar de webshop
                  </Link>
                </>}
            </div>}
        </motion.div>
      </div>
    </section>;
}